import React from 'react';

import { Typography } from '@mui/material';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { baseStatusOptions } from 'constants/options';
import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { BorrowersTestIds } from '../../../../../Borrowers.testIds';

import {
  bureausStyles,
  cellStyles,
  descriptionCellStyles,
  descriptionStyles,
} from './CreditReporting.styles';

export const getCreditReportingTableConfig = () => ({
  bureaus: {
    label: 'Bureaus',
    rowSpan: ({ bureaus }) => bureaus?.rowSpan,
    colSpan: ({ bureaus }) => bureaus?.colSpan,
    customStyles: ({ bureaus }) => (bureaus?.isDescription ? descriptionCellStyles : null),
    content: ({ bureaus }) => (
      <Typography
        sx={bureaus?.isDescription ? descriptionStyles : bureausStyles}
        variant="regular14"
      >
        {bureaus?.value}
      </Typography>
    ),
    isPermanent: false,
  },
  equifax: {
    label: 'Equifax',
    content: ({ equifax }) => equifax,
    customStyles: () => cellStyles,
    isPermanent: false,
  },
  experian: {
    label: 'Experian',
    content: ({ experian }) => experian,
    customStyles: () => cellStyles,
    isPermanent: false,
  },
  transUnion: {
    label: 'TransUnion',
    content: ({ transUnion }) => transUnion,
    customStyles: () => cellStyles,
    isPermanent: false,
  },
});

export const getCreditReportingConfigFields = ({ entityId }) => ({
  isPositiveRentalHistory: {
    fieldId: 'isPositiveRentalHistory',
    type: FieldType.RADIO,
    nameInModel: `[${entityId}].creditReporting.positiveRentalHistory`,
    inputProps: {
      labelPlacement: ItemLabelPlacement.END,
      align: ItemGroupAlignment.HORIZONTAL,
      radioGroupAlign: ItemGroupAlignment.HORIZONTAL,
      options: baseStatusOptions,
      label: (
        <Typography className="label" variant="regular14">
          Positive rental history:
        </Typography>
      ),
      disabled: false,
      required: false,
      variant: 'outlined',
      columns: 4,
      dataTestId: BorrowersTestIds.CREDIT_REPORTING_FORM_FIRST_NAME_SELECT,
      'data-test-value': 'first-name',
    },
  },
  caivrsNumber: {
    fieldId: 'caivrsNumber',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].creditReporting.caivrsNumber`,
    inputProps: {
      maxLength: 12,
      placeholder: '-',
      label: 'CAIVRS Number',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT_EXCEPT_SPECIAL_SYMBOLS,
    },
  },
  caivrsAuthorizationCode: {
    fieldId: 'caivrsAuthorizationCode',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].creditReporting.caivrsAuthorizationCode`,
    inputProps: {
      maxLength: 20,
      placeholder: '-',
      label: 'CAIVRS Authorization Code',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT_EXCEPT_SPECIAL_SYMBOLS,
    },
  },
});

export const getCreditReportingConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    entityId,
    section,
    isPositiveRentalHistoryVisible,
    isCaivrsNumberVisible,
    isCaivrsAuthorizationCodeVisible,
  }) => {
    const creditReportingConfigFields = getCreditReportingConfigFields({ entityId });

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: getConditionalArray(isPositiveRentalHistoryVisible, [
            creditReportingConfigFields.isPositiveRentalHistory,
          ]),
          key: `${entityId}-${section}-0`,
        },
        {
          type: FieldType.GROUP,
          fields: getConditionalArray(isCaivrsNumberVisible, [
            creditReportingConfigFields.caivrsNumber,
          ]),
          key: `${entityId}-${section}-1`,
        },
        {
          type: FieldType.GROUP,
          fields: getConditionalArray(isCaivrsAuthorizationCodeVisible, [
            creditReportingConfigFields.caivrsAuthorizationCode,
          ]),
          key: `${entityId}-${section}-2`,
        },
      ],
    };
  },
);
